import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Users,
  UserPlus,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertTriangle,
  ArrowUpRight,
  Calendar,
  Megaphone,
  GraduationCap,
  UserCog,
  ScrollText,
  ClipboardList,
  Eye
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============= SHARED WIDGETS =============
const EventsWidget = ({ events, primaryColor, navigate }) => (
  <Card className="border-0 shadow-sm">
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="text-lg font-display flex items-center gap-2">
        <Calendar className="h-5 w-5" style={{ color: primaryColor }} />
        Upcoming Events
      </CardTitle>
      <Button variant="link" className="p-0 h-auto text-sm" onClick={() => navigate("/events")} data-testid="view-all-events-btn">
        View all <ArrowUpRight className="ml-1 h-3 w-3" />
      </Button>
    </CardHeader>
    <CardContent>
      {events.length > 0 ? (
        <div className="space-y-3">
          {events.slice(0, 4).map(evt => (
            <div key={evt.event_id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg flex flex-col items-center justify-center text-white" style={{ backgroundColor: primaryColor }}>
                <span className="text-[10px] font-medium leading-none">{evt.event_date?.split("-")[1]}</span>
                <span className="text-lg font-bold leading-none">{evt.event_date?.split("-")[2]}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{evt.title}</p>
                {evt.event_time && <p className="text-xs text-muted-foreground">{evt.event_time}</p>}
                {evt.location && <p className="text-xs text-muted-foreground truncate">{evt.location}</p>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-8 text-center text-muted-foreground text-sm">No upcoming events</div>
      )}
    </CardContent>
  </Card>
);

const AnnouncementsWidget = ({ announcements, primaryColor, navigate }) => (
  <Card className="border-0 shadow-sm">
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="text-lg font-display flex items-center gap-2">
        <Megaphone className="h-5 w-5" style={{ color: primaryColor }} />
        Announcements
      </CardTitle>
      <Button variant="link" className="p-0 h-auto text-sm" onClick={() => navigate("/events")} data-testid="view-all-announcements-btn">
        View all <ArrowUpRight className="ml-1 h-3 w-3" />
      </Button>
    </CardHeader>
    <CardContent>
      {announcements.length > 0 ? (
        <div className="space-y-3">
          {announcements.slice(0, 3).map(ann => (
            <div key={ann.announcement_id} className={`p-3 rounded-lg border-l-4 ${
              ann.priority === "high" ? "border-l-red-500 bg-red-50" :
              ann.priority === "normal" ? "border-l-blue-500 bg-blue-50" :
              "border-l-slate-300 bg-slate-50"
            }`}>
              <p className="font-medium text-sm">{ann.title}</p>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{ann.content}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-8 text-center text-muted-foreground text-sm">No active announcements</div>
      )}
    </CardContent>
  </Card>
);

const FollowupsWidget = ({ followups, primaryColor, navigate }) => (
  <Card className="border-0 shadow-sm">
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="text-lg font-display flex items-center gap-2">
        <ClipboardList className="h-5 w-5" style={{ color: primaryColor }} />
        My Follow-ups
      </CardTitle>
      <Button variant="link" className="p-0 h-auto text-sm" onClick={() => navigate("/followups")}>
        View all <ArrowUpRight className="ml-1 h-3 w-3" />
      </Button>
    </CardHeader>
    <CardContent>
      {followups?.length > 0 ? (
        <div className="space-y-2">
          {followups.map(fu => (
            <div key={fu.followup_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{fu.task_description}</p>
                <p className="text-xs text-muted-foreground">Due: {fu.due_date}</p>
              </div>
              <Badge className={
                fu.status === "Overdue" ? "bg-red-100 text-red-700" :
                fu.status === "Open" ? "bg-orange-100 text-orange-700" :
                "bg-green-100 text-green-700"
              }>{fu.status}</Badge>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-6 text-center text-muted-foreground text-sm">No pending follow-ups</div>
      )}
    </CardContent>
  </Card>
);


// ============= MEMBER DASHBOARD =============
const MemberDashboard = ({ myData, primaryColor, navigate }) => {
  const training = myData?.my_training || {};
  const profile = myData?.member_profile;

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm" data-testid="member-stat-training">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Training Progress</p>
                <p className="text-3xl font-bold mt-1">{training.progress_pct || 0}%</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <GraduationCap className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <Progress value={training.progress_pct || 0} className="h-2 mt-3" />
            <p className="text-xs text-muted-foreground mt-1">{training.completed || 0}/{training.total || 0} topics</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm" data-testid="member-stat-members">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Chapter Members</p>
                <p className="text-3xl font-bold mt-1">{myData?.chapter_members || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <Button variant="link" className="p-0 h-auto mt-3 text-sm" onClick={() => navigate("/members")}>
              View directory <ArrowUpRight className="ml-1 h-3 w-3" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm" data-testid="member-stat-invited">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Visitors Invited</p>
                <p className="text-3xl font-bold mt-1">{myData?.my_visitors_invited || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm" data-testid="member-stat-followups">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Follow-ups</p>
                <p className="text-3xl font-bold mt-1">{myData?.my_followups?.length || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Training and Follow-ups */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {myData?.member_id && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-display flex items-center gap-2">
                <GraduationCap className="h-5 w-5" style={{ color: primaryColor }} />
                My Training
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-4">
                <div className="relative w-28 h-28 mx-auto">
                  <svg className="w-28 h-28 -rotate-90" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="#E2E8F0" strokeWidth="10" />
                    <circle cx="60" cy="60" r="52" fill="none" stroke={primaryColor} strokeWidth="10"
                      strokeDasharray={`${(training.progress_pct || 0) * 3.267} 326.7`}
                      strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold">{training.progress_pct || 0}%</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  {training.completed || 0} of {training.total || 0} topics completed
                </p>
                <Button variant="outline" className="mt-4" onClick={() => navigate(`/members/${myData.member_id}`)}>
                  <Eye className="mr-2 h-4 w-4" /> View Full Training
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <FollowupsWidget followups={myData?.my_followups} primaryColor={primaryColor} navigate={navigate} />
      </div>

      {/* Events & Announcements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EventsWidget events={myData?.upcoming_events || []} primaryColor={primaryColor} navigate={navigate} />
        <AnnouncementsWidget announcements={myData?.announcements || []} primaryColor={primaryColor} navigate={navigate} />
      </div>
    </div>
  );
};


// ============= HOST DASHBOARD =============
const HostDashboard = ({ myData, primaryColor, navigate }) => (
  <div className="space-y-6">
    {/* Stat Cards */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card className="border-0 shadow-sm" data-testid="host-stat-active-visitors">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active Visitors</p>
              <p className="text-3xl font-bold mt-1">{myData?.active_visitors || 0}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <UserPlus className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <Button variant="link" className="p-0 h-auto mt-3 text-sm" onClick={() => navigate("/visitors")}>
            View pipeline <ArrowUpRight className="ml-1 h-3 w-3" />
          </Button>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm" data-testid="host-stat-members">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Chapter Members</p>
              <p className="text-3xl font-bold mt-1">{myData?.chapter_members || 0}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <Users className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm" data-testid="host-stat-open-followups">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Open Follow-ups</p>
              <p className="text-3xl font-bold mt-1">{myData?.followup_stats?.open || 0}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          {(myData?.followup_stats?.overdue || 0) > 0 && (
            <div className="flex items-center gap-1 mt-3 text-sm">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-red-600 font-medium">{myData.followup_stats.overdue} overdue</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm" data-testid="host-stat-my-followups">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">My Follow-ups</p>
              <p className="text-3xl font-bold mt-1">{myData?.my_followups?.length || 0}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
              <ClipboardList className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    {/* Recent Visitors and Follow-ups */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-display flex items-center gap-2">
            <UserPlus className="h-5 w-5" style={{ color: primaryColor }} />
            Recent Visitors
          </CardTitle>
          <Button variant="link" className="p-0 h-auto text-sm" onClick={() => navigate("/visitors")}>
            View all <ArrowUpRight className="ml-1 h-3 w-3" />
          </Button>
        </CardHeader>
        <CardContent>
          {myData?.recent_visitors?.length > 0 ? (
            <div className="space-y-2">
              {myData.recent_visitors.map(v => (
                <div key={v.visitor_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => navigate(`/visitors/${v.visitor_id}`)}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{v.full_name}</p>
                    <p className="text-xs text-muted-foreground">{v.company || "No company"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      v.interest_level === 1 ? "bg-green-100 text-green-700" :
                      v.interest_level === 2 ? "bg-yellow-100 text-yellow-700" :
                      "bg-red-100 text-red-700"
                    }>L{v.interest_level}</Badge>
                    <Badge variant="outline">{v.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center text-muted-foreground text-sm">No recent visitors</div>
          )}
        </CardContent>
      </Card>

      <FollowupsWidget followups={myData?.my_followups} primaryColor={primaryColor} navigate={navigate} />
    </div>

    {/* Events & Announcements */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <EventsWidget events={myData?.upcoming_events || []} primaryColor={primaryColor} navigate={navigate} />
      <AnnouncementsWidget announcements={myData?.announcements || []} primaryColor={primaryColor} navigate={navigate} />
    </div>
  </div>
);


// ============= ADMIN DASHBOARD =============
const AdminDashboard = ({ myData, stats, visitorsByPT, monthlyStats, memberContributions, followupByMember, primaryColor, navigate }) => {
  const COLORS = [primaryColor, "#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"];
  const interestData = stats ? [
    { name: "Level 1 (Interested)", value: stats.interest_distribution.level_1, color: "#10B981" },
    { name: "Level 2 (Needs Permission)", value: stats.interest_distribution.level_2, color: "#F59E0B" },
    { name: "Level 3 (Not Now)", value: stats.interest_distribution.level_3, color: "#EF4444" }
  ] : [];

  return (
    <div className="space-y-6">
      {/* Admin Stats Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-total-visitors">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Visitors</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_visitors || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <span className="text-green-600 font-medium">{stats?.active_visitors || 0}</span>
              <span className="text-muted-foreground">active</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-total-members">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Members</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_members || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <Button variant="link" className="p-0 h-auto mt-3 text-sm" onClick={() => navigate("/members")}>
              View directory <ArrowUpRight className="ml-1 h-3 w-3" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-conversions">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Conversions</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_converted || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <span className="font-medium" style={{ color: primaryColor }}>{stats?.conversion_rate || 0}%</span>
              <span className="text-muted-foreground">rate</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-followups">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Follow-ups</p>
                <p className="text-3xl font-bold mt-1">{stats?.followup_stats?.open || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              {(stats?.followup_stats?.overdue || 0) > 0 ? (
                <><AlertTriangle className="h-4 w-4 text-red-500" /><span className="text-red-600 font-medium">{stats?.followup_stats?.overdue} overdue</span></>
              ) : (
                <><CheckCircle className="h-4 w-4 text-green-500" /><span className="text-green-600">All on track</span></>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admin Extra Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm" data-testid="stat-users">
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center">
              <UserCog className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{myData?.total_users || 0}</p>
              <p className="text-xs text-muted-foreground">System Users</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="stat-training-overview">
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center">
              <GraduationCap className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{myData?.training_overview?.members_started || 0}/{myData?.training_overview?.total_active_members || 0}</p>
              <p className="text-xs text-muted-foreground">Members Started Training</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="stat-audit">
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
              <ScrollText className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{myData?.total_audit_entries || 0}</p>
              <p className="text-xs text-muted-foreground">Audit Log Entries</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg font-display">Monthly Visitors & Conversions</CardTitle></CardHeader>
          <CardContent>
            {monthlyStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip /><Legend />
                  <Bar dataKey="visitors" name="Visitors" fill={primaryColor} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="conversions" name="Conversions" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">No data available yet</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg font-display">Interest Level Distribution</CardTitle></CardHeader>
          <CardContent>
            {interestData.some(d => d.value > 0) ? (
              <div className="flex items-center gap-8">
                <ResponsiveContainer width="50%" height={250}>
                  <PieChart>
                    <Pie data={interestData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value">
                      {interestData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-3">
                  {interestData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-sm">{item.name}</span>
                      </div>
                      <span className="font-semibold">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">No interest data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Visitors by Power Team & Follow-up Accountability */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg font-display">Visitors by Power Team</CardTitle></CardHeader>
          <CardContent>
            {visitorsByPT.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={visitorsByPT.slice(0, 6)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                  <Tooltip />
                  <Bar dataKey="count" fill={primaryColor} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">No data available</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg font-display">Follow-up Accountability</CardTitle></CardHeader>
          <CardContent>
            {followupByMember.length > 0 ? (
              <div className="space-y-3">
                {followupByMember.slice(0, 5).map((member, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                    <span className="text-sm font-medium truncate flex-1">{member.name}</span>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-green-600">{member.done} done</span>
                      <span className="text-orange-600">{member.open} open</span>
                      {member.overdue > 0 && <span className="text-red-600">{member.overdue} overdue</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">No follow-up data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Events & Announcements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EventsWidget events={myData?.upcoming_events || []} primaryColor={primaryColor} navigate={navigate} />
        <AnnouncementsWidget announcements={myData?.announcements || []} primaryColor={primaryColor} navigate={navigate} />
      </div>

      {/* Recent Audit */}
      {myData?.recent_audit?.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <ScrollText className="h-5 w-5" style={{ color: primaryColor }} />
              Recent Activity
            </CardTitle>
            <Button variant="link" className="p-0 h-auto text-sm" onClick={() => navigate("/audit-log")}>
              View full log <ArrowUpRight className="ml-1 h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {myData.recent_audit.map(log => (
                <div key={log.log_id} className="flex items-center justify-between p-2 rounded bg-slate-50 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{log.user_name}</span>
                    <Badge variant="outline" className="text-xs">{log.action}</Badge>
                    <span className="text-muted-foreground">{log.details}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{log.created_at?.split("T")[0]}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Member Contributions */}
      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-lg font-display">Top Member Contributions</CardTitle></CardHeader>
        <CardContent>
          {memberContributions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Member</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Visitors Invited</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Conversions</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Conv. Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {memberContributions.slice(0, 10).map((member, index) => (
                    <tr key={index} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium">{member.name}</td>
                      <td className="text-center py-3 px-4">{member.visitors_invited}</td>
                      <td className="text-center py-3 px-4">{member.conversions}</td>
                      <td className="text-center py-3 px-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          {member.visitors_invited > 0 ? Math.round((member.conversions / member.visitors_invited) * 100) : 0}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">No contribution data yet.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// ============= MAIN DASHBOARD =============
export const DashboardPage = () => {
  const { user, chapter } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [visitorsByPT, setVisitorsByPT] = useState([]);
  const [memberContributions, setMemberContributions] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [followupByMember, setFollowupByMember] = useState([]);
  const [myData, setMyData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";
  const role = user?.role || "member";

  useEffect(() => {
    fetchDashboardData();
  }, [user?.chapter_id]);

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Always fetch role-based dashboard
      const myDashRes = await fetch(`${API}/dashboard/my-dashboard`, { headers });
      if (myDashRes.ok) setMyData(await myDashRes.json());

      // For admin, also fetch full analytics
      if (role === "admin") {
        const [statsRes, ptRes, contribRes, monthlyRes, fuRes] = await Promise.all([
          fetch(`${API}/dashboard/stats`, { headers }),
          fetch(`${API}/dashboard/visitors-by-powerteam`, { headers }),
          fetch(`${API}/dashboard/member-contributions`, { headers }),
          fetch(`${API}/dashboard/monthly-stats`, { headers }),
          fetch(`${API}/dashboard/followup-by-member`, { headers })
        ]);
        if (statsRes.ok) setStats(await statsRes.json());
        if (ptRes.ok) setVisitorsByPT(await ptRes.json());
        if (contribRes.ok) setMemberContributions(await contribRes.json());
        if (monthlyRes.ok) setMonthlyStats(await monthlyRes.json());
        if (fuRes.ok) setFollowupByMember(await fuRes.json());
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const roleLabels = { admin: "Admin Overview", host: "Visitor Host Dashboard", member: "My Dashboard" };

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Welcome back, {user?.name}!
            </p>
            <Badge variant="outline" className="text-xs mt-1" data-testid="role-badge">
              {roleLabels[role] || "Member"}
            </Badge>
          </div>
        </div>

        {role === "admin" && (
          <AdminDashboard
            myData={myData} stats={stats} visitorsByPT={visitorsByPT}
            monthlyStats={monthlyStats} memberContributions={memberContributions}
            followupByMember={followupByMember} primaryColor={primaryColor} navigate={navigate}
          />
        )}
        {role === "host" && (
          <HostDashboard myData={myData} primaryColor={primaryColor} navigate={navigate} />
        )}
        {role === "member" && (
          <MemberDashboard myData={myData} primaryColor={primaryColor} navigate={navigate} />
        )}
      </div>
    </Layout>
  );
};

export default DashboardPage;
